import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MessageCircle, Bell, Phone } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { buildWhatsAppLink } from "@/lib/activity-utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  nurseryId: string | null;
  nurseryName: string | null;
  ownerUserId: string | null;
  ownerPhone: string | null;
  currentStep?: string;
}

const QuickContactDialog = ({ open, onOpenChange, nurseryId, nurseryName, ownerUserId, ownerPhone, currentStep }: Props) => {
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(ownerPhone || "");
    setMessage(
      `مرحباً ${nurseryName || ""}،\nأنا من فريق دعم NurseryNet 👋\n${
        currentStep ? `لاحظنا أنك تعمل على: ${currentStep}.\n` : ""
      }هل تحتاج لأي مساعدة؟ نحن متاحون الآن.`
    );
  }, [open, ownerPhone, nurseryName, currentStep]);

  const sendWhatsApp = () => {
    const url = buildWhatsAppLink(phone, message);
    if (!url) {
      toast.error("رقم هاتف غير صالح");
      return;
    }
    window.open(url, "_blank");
  };

  const sendNotification = async () => {
    if (!ownerUserId || !nurseryId) {
      toast.error("لا يمكن إرسال إشعار — بيانات ناقصة");
      return;
    }
    setSending(true);
    const { error } = await supabase.from("notifications").insert({
      user_id: ownerUserId,
      nursery_id: nurseryId,
      title: "📩 رسالة من فريق الدعم",
      body: message,
      type: "support_message",
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("تم إرسال الإشعار");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            تواصل مع {nurseryName || "الحضانة"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> رقم الواتساب
            </Label>
            <Input
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="20xxxxxxxxxx"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">الرسالة</Label>
            <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
          <Button variant="secondary" onClick={sendNotification} disabled={sending} className="gap-2">
            <Bell className="w-4 h-4" /> إشعار داخل التطبيق
          </Button>
          <Button onClick={sendWhatsApp} className="gap-2 bg-green-600 hover:bg-green-700 text-white">
            <MessageCircle className="w-4 h-4" /> إرسال واتساب
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickContactDialog;
